import React, { useState } from 'react';
import './OnboardingForm.css';
import FileUpload from './FileUpload';
import { useToast } from './ToastContainer';
import ConfirmationModal from './ConfirmationModal';
import { useCreateAgent } from '../hooks/useDashboard';

interface FormData {
  businessName: string;
  businessType: string;
  businessDescription: string;
  tone: string;
  faqsPolicies: string;
  extractedDocuments: string[];
  locationInfo?: {
    businessName?: string;
    phone?: string;
    address?: string;
    businessHours?: {
      start?: string;
      end?: string;
    };
    timezone?: string;
  };
}

const OnboardingForm: React.FC = () => {
  const { showSuccess, showError } = useToast();
  const createAgentMutation = useCreateAgent();
  const [formData, setFormData] = useState<FormData>({
    businessName: '',
    businessType: '',
    businessDescription: '',
    tone: 'friendly',
    faqsPolicies: '',
    extractedDocuments: []
  });
  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [subscriptionMessage, setSubscriptionMessage] = useState('');
  const [generatedAgent, setGeneratedAgent] = useState<{id: string, businessName: string} | null>(null);

  const businessTypes = [
    { value: 'salon', label: 'Salon & Spa' },
    { value: 'gym', label: 'Fitness & Wellness' }, 
    { value: 'bakery', label: 'Bakery & Cafe' },
    { value: 'automotive', label: 'Automotive Services' },
    { value: 'cleaning', label: 'Cleaning Services' },
    { value: 'pet', label: 'Pet Services' },
    { value: 'photography', label: 'Photography' }
  ];

  const toneOptions = [
    { value: 'friendly', label: 'Friendly' },
    { value: 'professional', label: 'Professional' },
    { value: 'chill', label: 'Chill' },
    { value: 'formal', label: 'Formal' },
    { value: 'casual', label: 'Casual' }
  ];

  const validateForm = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.businessName.trim()) {
      newErrors.businessName = 'Business name is required';
    }
    
    if (!formData.businessType) {
      newErrors.businessType = 'Business type is required';
    }
    
    if (!formData.businessDescription.trim()) {
      newErrors.businessDescription = 'Business description is required';
    } else if (formData.businessDescription.trim().length < 20) {
      newErrors.businessDescription = 'Please provide a more detailed description (at least 20 characters)';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleFilesProcessed = (extractedTexts: string[]) => {
    setFormData(prev => ({
      ...prev,
      extractedDocuments: extractedTexts
    }));
  };

  const handleLocationInfoChange = (field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      locationInfo: {
        ...prev.locationInfo,
        [field]: value
      }
    }));
  };

  const handleAddressChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      locationInfo: {
        ...prev.locationInfo,
        address: e.target.value
      }
    }));
  };

  const handleBusinessHoursChange = (field: 'start' | 'end', value: string) => {
    setFormData(prev => ({
      ...prev,
      locationInfo: {
        ...prev.locationInfo,
        businessHours: {
          ...prev.locationInfo?.businessHours,
          [field]: value
        }
      }
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      const result = await createAgentMutation.mutateAsync(formData);
      
      if (result.agent) {
        setGeneratedAgent({
          id: result.agent.id,
          businessName: result.agent.businessName || formData.businessName
        });
        setShowSuccessModal(true);
        // Reset form
        setFormData({
          businessName: '',
          businessType: '',
          businessDescription: '',
          tone: 'friendly',
          faqsPolicies: '',
          extractedDocuments: []
        });
      }
    } catch (error: any) {
      console.error('Error generating agent:', error);
      
      // Handle subscription requirement
      if (error.message && error.message.includes('subscription')) {
        setSubscriptionMessage(error.message);
        setShowSubscriptionModal(true);
        return;
      }
      
      const errorMessage = error instanceof Error ? error.message : 'Unknown error occurred';
      showError(`Error generating agent: ${errorMessage}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!generatedAgent) return;
    
    const chatUrl = `${window.location.origin}/chat/${generatedAgent.id}`;
    try {
      await navigator.clipboard.writeText(chatUrl);
      showSuccess('Chat link copied to clipboard!');
    } catch (error) {
      console.error('Failed to copy to clipboard:', error);
      showError('Failed to copy link to clipboard');
    }
  };

  const handleCloseModal = () => {
    setShowSuccessModal(false);
    setGeneratedAgent(null);
  };

  const handleSubscriptionConfirm = () => {
    setShowSubscriptionModal(false);
    // Navigate to home page with pricing hash - the ScrollToTop component will handle this properly
    window.location.href = '/#pricing';
  };

  const handleSubscriptionCancel = () => {
    setShowSubscriptionModal(false);
  };

  return (
    <div className="max-w-6xl mx-auto px-5 py-5 font-sans leading-relaxed">
      <div className="text-center mb-10">
        <h1 className="text-gray-800 dark:text-white text-3xl md:text-4xl lg:text-5xl font-semibold mb-2">Create Your <span className="bg-gradient-to-r from-primary-500 to-secondary-500 bg-clip-text text-transparent">AI Agent</span></h1>
        <p className="text-gray-600 dark:text-gray-300 text-lg md:text-xl">Fill out this form to generate your custom chatbot in minutes</p>
      </div>
      
      <form onSubmit={handleSubmit} className="bg-white dark:bg-gray-800 rounded-xl p-8 md:p-10 lg:p-12 shadow-lg border border-gray-200 dark:border-gray-700">
        <div className="mb-6">
          <label htmlFor="businessName" className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">Business Name *</label>
          <input
            type="text"
            id="businessName"
            name="businessName"
            value={formData.businessName}
            onChange={handleInputChange}
            placeholder="e.g., Sarah's Hair Studio"
            className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 ${errors.businessName ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'} focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30`}
            required
          />
          {errors.businessName && <span className="block text-red-500 text-sm mt-1 font-medium">{errors.businessName}</span>}
        </div>

        <div className="mb-6">
          <label htmlFor="businessType" className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">Business Type *</label>
          <select
            id="businessType"
            name="businessType"
            value={formData.businessType}
            onChange={handleInputChange}
            className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white ${errors.businessType ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'} focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30`}
            required
          >
            <option value="">Select your business type</option>
            {businessTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          {errors.businessType && <span className="block text-red-500 text-sm mt-1 font-medium">{errors.businessType}</span>}
        </div>

        <div className="mb-6">
          <label htmlFor="businessDescription" className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">Business Description *</label>
          <textarea
            id="businessDescription"
            name="businessDescription"
            value={formData.businessDescription}
            onChange={handleInputChange}
            placeholder="Describe your business, services, and what makes you special..."
            rows={4}
            className={`w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-vertical min-h-[100px] ${errors.businessDescription ? 'border-red-500 bg-red-50 dark:bg-red-900/20' : 'border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400'} focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30`}
            required
          />
          {errors.businessDescription && <span className="block text-red-500 text-sm mt-1 font-medium">{errors.businessDescription}</span>}
        </div>

        <div className="mb-6">
          <label htmlFor="tone" className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">Agent Tone *</label>
          <select
            id="tone"
            name="tone"
            value={formData.tone}
            onChange={handleInputChange}
            className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30"
            required
          >
            {toneOptions.map(option => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </div>

        {/* Business Information Section */}
        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <div className="mb-4">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">Business Information</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              Add your business details for customer inquiries and appointment booking
            </p>
          </div>

          <div className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">
                  Business Hours - Opening Time
                </label>
                <input
                  type="time"
                  value={formData.locationInfo?.businessHours?.start || '09:00'}
                  onChange={(e) => handleBusinessHoursChange('start', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30"
                />
              </div>

              <div>
                <label className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">
                  Business Hours - Closing Time
                </label>
                <input
                  type="time"
                  value={formData.locationInfo?.businessHours?.end || '17:00'}
                  onChange={(e) => handleBusinessHoursChange('end', e.target.value)}
                  className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30"
                />
              </div>
            </div>

            <div>
              <label className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">
                Phone Number (Optional)
              </label>
              <input
                type="tel"
                value={formData.locationInfo?.phone || ''}
                onChange={(e) => handleLocationInfoChange('phone', e.target.value)}
                placeholder="(555) 123-4567"
                className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30"
              />
            </div>

            <div>
              <label className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">
                Business Address (Optional)
              </label>
              <input
                type="text"
                value={formData.locationInfo?.address || ''}
                onChange={handleAddressChange}
                placeholder="123 Main Street, City, State 12345"
                className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Enter your business address (street, city, state, zip code)
              </p>
            </div>
          </div>
        </div>

        <div className="mb-6">
          <label htmlFor="faqsPolicies" className="block mb-2 text-gray-700 dark:text-gray-300 font-medium text-sm">FAQs, Policies, or Menu (Optional)</label>
          <textarea
            id="faqsPolicies"
            name="faqsPolicies"
            value={formData.faqsPolicies}
            onChange={handleInputChange}
            placeholder="Add any frequently asked questions, policies, menu items, or other important information..."
            rows={6}
            className="w-full px-4 py-3 border-2 rounded-lg text-base transition-all duration-200 box-border font-inherit bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 resize-vertical min-h-[100px] border-gray-300 dark:border-gray-600 focus:border-blue-500 dark:focus:border-blue-400 focus:outline-none focus:ring-3 focus:ring-blue-100 dark:focus:ring-blue-900/30"
          />
        </div>

        <div className="mb-6">
          <label className="block mb-3 text-gray-700 dark:text-gray-300 font-medium">
            Or Upload Business Documents
          </label>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            Upload your existing menus, price lists, policies, or other business documents. Our AI will extract the text and use it to train your agent.
          </p>
          <FileUpload onFilesProcessed={handleFilesProcessed} />
          
          {formData.extractedDocuments.length > 0 && (
            <div className="mt-4 p-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"/>
                </svg>
                <span className="text-sm font-medium text-green-800 dark:text-green-200">
                  {formData.extractedDocuments.length} document(s) processed successfully
                </span>
              </div>
              <p className="text-xs text-green-700 dark:text-green-300 mt-1">
                Your AI agent will use this information to provide accurate responses to customers.
              </p>
            </div>
          )}
        </div>

        {/* Old location override section removed - business info now in main form above */}

        <button 
          type="submit" 
          disabled={isSubmitting}
          className="w-full bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white py-4 px-6 rounded-lg font-medium text-lg transition-all duration-200 hover:shadow-lg hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 disabled:hover:shadow-none focus:outline-none focus:ring-4 focus:ring-primary-200 dark:focus:ring-primary-800"
        >
          {isSubmitting ? 'Generating Agent...' : 'Generate Agent'}
        </button>
      </form>

      {/* Success Modal */}
      {showSuccessModal && generatedAgent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4">
            <div className="p-6">
              {/* Success Icon */}
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>

              {/* Content */}
              <div className="text-center mb-6">
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">
                  🎉 Agent Created Successfully!
                </h3>
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Your AI agent for <span className="font-medium text-gray-900 dark:text-white">{generatedAgent.businessName}</span> is ready to help your customers.
                </p>
                
                {/* Chat Link */}
                <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 mb-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Your Chat Link:
                  </label>
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      readOnly
                      value={`${window.location.origin}/chat/${generatedAgent.id}`}
                      className="flex-1 px-3 py-2 text-sm bg-white dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded-lg text-gray-900 dark:text-white"
                    />
                    <button
                      onClick={handleCopyLink}
                      className="px-3 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white text-sm font-medium rounded-lg transition-all duration-200"
                    >
                      Copy
                    </button>
                  </div>
                </div>

                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">
                  Share this link with your customers or embed it on your website to start receiving AI-powered support requests.
                </p>

                {/* Services Approval Notice */}
                <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-4">
                  <div>
                    <p className="text-sm font-medium text-blue-800 dark:text-blue-200">
                      Next Step: Review Your Services
                    </p>
                    <p className="text-xs text-blue-700 dark:text-blue-300 mt-1">
                      Your AI has automatically extracted services from your business description. Please review and approve them in <strong>Agent Settings → Services</strong> for accurate booking and analytics.
                    </p>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-3">
                <a
                  href="/dashboard"
                  className="flex-1 inline-flex items-center justify-center px-4 py-2 bg-gradient-to-r from-primary-500 to-secondary-500 hover:from-primary-600 hover:to-secondary-600 text-white text-sm font-medium rounded-lg transition-all duration-200"
                >
                  Go to Dashboard
                </a>
                <button
                  onClick={handleCloseModal}
                  className="flex-1 px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 text-sm font-medium rounded-lg transition-all duration-200"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Subscription Required Modal */}
      <ConfirmationModal
        isOpen={showSubscriptionModal}
        onClose={handleSubscriptionCancel}
        onConfirm={handleSubscriptionConfirm}
        title="Subscription Required"
        message={`${subscriptionMessage}${subscriptionMessage.endsWith('.') ? '' : '.'}\n\nWould you like to subscribe now?`}
        confirmText="Subscribe Now"
        cancelText="Cancel"
        type="warning"
      />
    </div>
  );
};

export default OnboardingForm;